import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { REPORTS } from "@/constants/testIds";
import { toast } from "sonner";

export default function Reports() {
  const { t } = useI18n();
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState("all");
  const [country, setCountry] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
  }, []);

  const download = async (format) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (propId !== "all") params.append("property_id", propId);
      if (country !== "all") params.append("country", country);
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);
      const path = format === "excel" ? "/reports/excel" : "/reports/pdf";
      const resp = await api.get(`${path}?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "excel" ? "reporte.xlsx" : "reporte.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch (e) {
      toast.error("No se pudo generar el reporte");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("nav.reports")}</h1>
        <div className="text-sm text-muted-foreground mt-1">
          Exportá tus movimientos consolidados en USD, con filtros por propiedad, país y período.
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-6 space-y-5 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Propiedad</Label>
            <Select value={propId} onValueChange={setPropId}>
              <SelectTrigger data-testid={REPORTS.filterProperty}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>País</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid={REPORTS.filterCountry}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="AR">Argentina</SelectItem>
                <SelectItem value="US">EE.UU.</SelectItem>
                <SelectItem value="MX">México</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("reports.from")}</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid={REPORTS.filterStart} />
          </div>
          <div>
            <Label>{t("reports.to")}</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid={REPORTS.filterEnd} />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            className="rounded-full"
            onClick={() => download("excel")}
            disabled={downloading}
            data-testid={REPORTS.exportExcel}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> {t("reports.excel")}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => download("pdf")}
            disabled={downloading}
            data-testid={REPORTS.exportPdf}
          >
            <FileDown className="w-4 h-4 mr-2" /> {t("reports.pdf")}
          </Button>
        </div>
      </div>
    </div>
  );
}
